import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Plus, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import agendaExcecoes from "../../../api/agendaExcecoes";
import PainelDatePicker from "../../../components/PainelDatePicker";
import PainelMonthPicker from "../../../components/PainelMonthPicker";
import FilaZeroSelect from "../../../components/FilaZeroSelect";
import FilaZeroTimeSelect from "../../../components/FilaZeroTimeSelect";
import "./componentes.css";

const TIPOS = [
  { value: "dia_fechado", label: "Dia fechado (folga / feriado)" },
  { value: "ferias", label: "Férias (período)" },
  { value: "horario_especial", label: "Horário especial (abrir em feriado)" },
  { value: "bloqueio_parcial", label: "Bloqueio parcial (última hora)" },
];

const LABEL_TIPO = Object.fromEntries(TIPOS.map((item) => [item.value, item.label]));

const formatarHora = (valor) => String(valor || "").slice(0, 5);

const formatarPeriodo = (item) => {
  const inicio = dayjs(item.data).format("DD/MM/YYYY");
  if (item.tipo === "ferias" && item.data_fim) {
    const fim = dayjs(item.data_fim).format("DD/MM/YYYY");
    return `${inicio} — ${fim}`;
  }
  return inicio;
};

const mensagemErro = (erro, fallback) =>
  erro?.response?.data?.message ||
  erro?.response?.data?.mensagem ||
  erro?.message ||
  fallback;

const ConfiguracaoAgendaExcecoes = ({ estabelecimentoId }) => {
  const [mesReferencia, setMesReferencia] = useState(dayjs().format("YYYY-MM"));
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    data: dayjs().format("YYYY-MM-DD"),
    data_fim: dayjs().add(6, "day").format("YYYY-MM-DD"),
    tipo: "dia_fechado",
    hora_inicio: "09:00",
    hora_fim: "18:00",
    motivo: "",
  });

  const carregar = useCallback(async () => {
    if (!estabelecimentoId) return;
    try {
      setLoading(true);
      const resposta = await agendaExcecoes.listar(estabelecimentoId, mesReferencia);
      setLista(Array.isArray(resposta?.excecoes) ? resposta.excecoes : []);
    } catch (erro) {
      toast.error(mensagemErro(erro, "Não foi possível carregar as exceções."));
    } finally {
      setLoading(false);
    }
  }, [estabelecimentoId, mesReferencia]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const precisaHorario = form.tipo === "horario_especial" || form.tipo === "bloqueio_parcial";
  const precisaPeriodo = form.tipo === "ferias";

  const resumoForm = useMemo(() => {
    if (form.tipo === "dia_fechado") {
      return "Nenhum horário ficará disponível nesta data.";
    }
    if (form.tipo === "ferias") {
      return `Nenhum agendamento poderá ser criado entre ${dayjs(form.data).format("DD/MM/YYYY")} e ${dayjs(form.data_fim).format("DD/MM/YYYY")}.`;
    }
    if (form.tipo === "horario_especial") {
      return `Somente ${form.hora_inicio} às ${form.hora_fim} estarão liberados nesta data.`;
    }
    return `Os horários entre ${form.hora_inicio} e ${form.hora_fim} ficarão bloqueados nesta data.`;
  }, [form]);

  const salvar = async () => {
    if (!estabelecimentoId || salvando) return;
    if (!form.data) {
      toast.error("Informe a data da exceção.");
      return;
    }

    if (precisaPeriodo) {
      if (!form.data_fim) {
        toast.error("Informe a data final das férias.");
        return;
      }
      if (dayjs(form.data_fim).isBefore(dayjs(form.data), "day")) {
        toast.error("A data final deve ser igual ou posterior à data inicial.");
        return;
      }
    }

    try {
      setSalvando(true);
      await agendaExcecoes.criar(estabelecimentoId, {
        data: form.data,
        data_fim: precisaPeriodo ? form.data_fim : null,
        tipo: form.tipo,
        hora_inicio: precisaHorario ? form.hora_inicio : null,
        hora_fim: precisaHorario ? form.hora_fim : null,
        motivo: form.motivo?.trim() || null,
      });
      toast.success("Exceção de agenda salva com sucesso.");
      setMesReferencia(dayjs(form.data).format("YYYY-MM"));
      await carregar();
    } catch (erro) {
      toast.error(mensagemErro(erro, "Não foi possível salvar a exceção."));
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (excecao) => {
    const periodoFormatado = formatarPeriodo(excecao);
    const titulo =
      excecao.tipo === "ferias" ? "Remover período de férias?" : "Remover exceção?";
    const resultado = await Swal.fire({
      title: titulo,
      html: `A exceção de <strong>${periodoFormatado}</strong> será removida da agenda.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#667085",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!resultado.isConfirmed) return;
    try {
      await agendaExcecoes.remover(estabelecimentoId, excecao.id);
      toast.success("Exceção removida com sucesso.");
      await carregar();
    } catch (erro) {
      toast.error(mensagemErro(erro, "Não foi possível remover a exceção."));
    }
  };

  return (
    <section className="secao-config">
      <div className="section-heading">
        <div>
          <span className="config-eyebrow">EXCEÇÕES POR DATA</span>
          <h2>Exceções de agenda</h2>
          <p>
            Use para folga, feriado, férias, feriado aberto ou bloqueio de última hora.
            A grade semanal continua valendo nos demais dias.
          </p>
        </div>
      </div>

      <div className="config-grid-2 agenda-excecao-form">
        <label className="field-label">
          {precisaPeriodo ? "Data inicial" : "Data"}
          <PainelDatePicker
            name="data"
            value={form.data}
            onChange={(event) =>
              setForm((atual) => {
                const proximaData = event.target.value;
                const atualizacao = { ...atual, data: proximaData };
                if (
                  atual.tipo === "ferias" &&
                  dayjs(atual.data_fim).isBefore(dayjs(proximaData), "day")
                ) {
                  atualizacao.data_fim = proximaData;
                }
                return atualizacao;
              })
            }
          />
        </label>

        {precisaPeriodo && (
          <label className="field-label">
            Data final
            <PainelDatePicker
              name="data_fim"
              value={form.data_fim}
              minDate={form.data}
              onChange={(event) =>
                setForm((atual) => ({ ...atual, data_fim: event.target.value }))
              }
            />
          </label>
        )}

        <label className="field-label">
          Tipo
          <FilaZeroSelect
            name="tipo"
            value={form.tipo}
            options={TIPOS}
            onChange={(event) => {
              const proximoTipo = event.target.value;
              setForm((atual) => {
                const atualizacao = { ...atual, tipo: proximoTipo };
                if (proximoTipo === "ferias" && !atual.data_fim) {
                  atualizacao.data_fim = dayjs(atual.data).add(6, "day").format("YYYY-MM-DD");
                }
                return atualizacao;
              });
            }}
            isSearchable={false}
            ariaLabel="Tipo de exceção"
          />
        </label>

        {precisaHorario && (
          <>
            <label className="field-label">
              Início
              <FilaZeroTimeSelect
                name="hora_inicio"
                value={form.hora_inicio}
                onChange={(event) =>
                  setForm((atual) => ({
                    ...atual,
                    hora_inicio: event.target.value,
                  }))
                }
              />
            </label>

            <label className="field-label">
              Fim
              <FilaZeroTimeSelect
                name="hora_fim"
                value={form.hora_fim}
                onChange={(event) =>
                  setForm((atual) => ({
                    ...atual,
                    hora_fim: event.target.value,
                  }))
                }
              />
            </label>
          </>
        )}

        <label className="field-label agenda-excecao-motivo">
          Motivo (opcional)
          <input
            type="text"
            className="field"
            value={form.motivo}
            onChange={(event) =>
              setForm((atual) => ({ ...atual, motivo: event.target.value }))
            }
            placeholder={
              precisaPeriodo
                ? "Ex.: Férias coletivas, reforma, recesso"
                : "Ex.: Feriado, folga, compromisso pessoal"
            }
          />
        </label>
      </div>

      <p className="agenda-excecao-resumo">{resumoForm}</p>

      <button type="button" className="btn" onClick={salvar} disabled={salvando}>
        <Plus size={16} />
        {salvando ? "Salvando..." : "Salvar exceção"}
      </button>

      <div className="agenda-excecao-list-header">
        <div className="field-label">
          <span>Mês</span>
          <PainelMonthPicker
            name="mes_referencia"
            value={mesReferencia}
            onChange={(event) => setMesReferencia(event.target.value)}
            placeholder="Selecione o mês"
          />
        </div>
      </div>

      {loading ? (
        <p className="muted">Carregando exceções...</p>
      ) : lista.length === 0 ? (
        <p className="muted">Nenhuma exceção cadastrada neste mês.</p>
      ) : (
        <div className="agenda-excecao-list">
          {lista.map((item) => (
            <article key={item.id} className="agenda-excecao-item">
              <div>
                <strong>{formatarPeriodo(item)}</strong>
                <span>{LABEL_TIPO[item.tipo] || item.tipo}</span>
                {item.hora_inicio && item.hora_fim && (
                  <span>
                    {formatarHora(item.hora_inicio)} - {formatarHora(item.hora_fim)}
                  </span>
                )}
                {item.motivo && <span>{item.motivo}</span>}
              </div>
              <button
                type="button"
                className="agenda-excecao-remove"
                onClick={() => remover(item)}
                aria-label={`Remover exceção de ${formatarPeriodo(item)}`}
              >
                <Trash2 size={16} />
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default ConfiguracaoAgendaExcecoes;
